import React, { useState, useEffect } from 'react';

// --- SUB-COMPONENT: Individual Product Card ---
const ProductCard = ({ product, onAddToCart }) => {
  const sizeArray = product.sizes ? product.sizes.split(',') : [];
  const [selectedSize, setSelectedSize] = useState(sizeArray.length > 0 ? sizeArray[0] : null);

  const handleAdd = () => {
    onAddToCart({ ...product, selectedSize });
  };

  return (
    <div className="bg-emerald-900/30 backdrop-blur-sm border border-golden/20 rounded-xl p-3 flex flex-col hover:border-golden/60 transition-colors h-full">
      <img src={product.imageUrl} alt={product.title} className="w-full aspect-square object-cover mb-3 rounded-lg bg-black/20" />
      <span className="text-[10px] uppercase tracking-wider text-golden/60 mb-1">{product.category}</span>
      <h3 className="text-golden font-bold mb-1 truncate">{product.title}</h3>
      <div className="text-lg font-black text-white mb-2">₹{product.price}</div>
      
      {/* SIZE SELECTOR UI */}
      {sizeArray.length > 0 && (
        <div className="mb-3 mt-auto">
          <div className="text-[10px] text-white/60 mb-1">Select Size:</div>
          <div className="flex flex-wrap gap-1">
            {sizeArray.map(size => (
              <button
                key={size}
                onClick={() => setSelectedSize(size)}
                className={`text-xs px-2 py-1 rounded border transition-colors ${
                  selectedSize === size 
                    ? 'bg-golden text-emerald-950 border-golden font-bold' 
                    : 'bg-transparent text-white/70 border-white/20 hover:border-golden/50'
                }`}
              >
                {size}
              </button>
            ))}
          </div>
        </div>
      )}
      
      <button 
        onClick={handleAdd} 
        className="mt-3 w-full bg-emerald-800 border border-golden/40 text-golden py-2.5 rounded-lg font-bold hover:bg-golden hover:text-emerald-950 transition-colors"
      >
        Add to Cart
      </button>
    </div>
  );
};

// --- MAIN APP COMPONENT ---
function App() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState(["All"]);
  const [isLoading, setIsLoading] = useState(true);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  
  const [cart, setCart] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const productsPerPage = 8;

  const waNumber = "918686866875";

  // FETCH LIVE DATA
  useEffect(() => {
    fetch('https://keralaindex.in/whatsappstore/api.php')
      .then(response => response.json())
      .then(data => {
        setProducts(data);
        const uniqueCategories = ["All", ...new Set(data.map(item => item.category))];
        setCategories(uniqueCategories);
        setIsLoading(false);
      })
      .catch(error => {
        console.error("Error fetching live products:", error);
        setIsLoading(false);
      });
  }, []);

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = activeCategory === 'All' || product.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = filteredProducts.slice(indexOfFirstProduct, indexOfLastProduct);
  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);

  // CART LOGIC
  const handleAddToCart = (product) => {
    setCart(prev => {
      const existing = prev.find(item => item.id === product.id && item.selectedSize === product.selectedSize);
      if (existing) {
        return prev.map(item => 
          (item.id === product.id && item.selectedSize === product.selectedSize) 
            ? { ...item, qty: item.qty + 1 } 
            : item
        );
      }
      return [...prev, { ...product, qty: 1 }];
    });
  };

  const removeFromCart = (id, selectedSize) => {
    setCart(cart.filter(item => !(item.id === id && item.selectedSize === selectedSize)));
  };
  
  const cartTotal = cart.reduce((sum, item) => sum + (parseFloat(item.price) * item.qty), 0);

  // PAYMENT LOGIC
  const handleCheckoutClick = () => {
    if (cart.length === 0) return;
    
    // YOUR EXACT UPI ID IS SET HERE
    const upiId = "8686866875@ibl"; 
    
    const upiLink = `upi://pay?pa=${encodeURIComponent(upiId)}&pn=${encodeURIComponent('WhatsAppStore')}&am=${encodeURIComponent(cartTotal.toFixed(2))}&cu=INR`;
    window.location.href = upiLink;
    setIsCartOpen(false);
    setShowPaymentModal(true);
  };

  const handleSendScreenshot = () => {
    const orderList = cart.map(item => {
      const sizeText = item.selectedSize ? ` (Size: ${item.selectedSize})` : '';
      return `- ${item.qty}x ${item.title}${sizeText} (₹${item.price})`;
    }).join('%0A');

    const message = `Hi there! 👋 I'd like to confirm my order.%0A%0A*Order Summary:*%0A${orderList}%0A%0A*Total Paid:* ₹${cartTotal.toFixed(2)}%0A%0AI have completed the UPI payment. Please find my receipt attached below. Thank you! ✨`;
    
    window.open(`https://wa.me/${waNumber}?text=${message}`, '_blank');
    setShowPaymentModal(false);
    setCart([]); 
  };

  return (
    <div className="bg-emerald-950 text-golden min-h-screen font-sans flex flex-col relative">
      <header className="sticky top-0 z-40 bg-emerald-950/90 backdrop-blur-md border-b border-golden/20 shadow-md">
        <div className="max-w-7xl mx-auto p-4 flex flex-wrap justify-between items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-golden rounded-lg flex items-center justify-center text-emerald-950 font-black text-xl">W</div>
            <span className="font-extrabold text-2xl tracking-tight hidden sm:block">WhatsAppStore</span>
          </div>
          <div className="flex-1 max-w-md mx-auto order-3 w-full sm:order-none sm:w-auto">
             <input type="text" placeholder="Search products..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="w-full bg-emerald-900/60 text-white p-2.5 rounded-lg border border-golden/30 focus:ring-2 focus:ring-golden/50 outline-none placeholder:text-golden/50" />
          </div>
          <button onClick={() => setIsCartOpen(true)} className="relative bg-emerald-900 border border-golden/50 p-2.5 rounded-lg hover:bg-emerald-800 transition-colors flex items-center gap-2">
            🛒 <span className="font-bold hidden sm:inline">Cart</span>
            {cart.length > 0 && <span className="absolute -top-2 -right-2 bg-golden text-emerald-950 rounded-full w-5 h-5 text-xs flex items-center justify-center font-black animate-bounce">{cart.length}</span>}
          </button>
        </div>
      </header>

      <main className="flex-1 p-4 md:p-8 max-w-7xl mx-auto w-full">
        {isLoading ? (
            <div className="text-center py-20 text-golden/50 animate-pulse text-xl">Loading live products...</div>
        ) : (
            <>
                <div className="flex gap-3 overflow-x-auto pb-4 mb-6 scrollbar-hide">
                {categories.map(cat => (
                    <button key={cat} onClick={() => { setActiveCategory(cat); setCurrentPage(1); }} className={`whitespace-nowrap px-5 py-2 rounded-full text-sm font-semibold border transition-all ${activeCategory === cat ? 'bg-golden text-emerald-950 border-golden' : 'bg-transparent text-golden border-golden/30 hover:bg-emerald-900'}`}>{cat}</button>
                ))}
                </div>

                {currentProducts.length === 0 ? (
                <div className="text-center py-20 text-golden/50 border border-dashed border-golden/30 rounded-xl">No products added yet. Add some from your admin panel!</div>
                ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6 mb-10">
                    {currentProducts.map((product) => (
                       <ProductCard key={product.id} product={product} onAddToCart={handleAddToCart} />
                    ))}
                </div>
                )}

                {totalPages > 1 && (
                <div className="flex justify-center gap-2 mt-8">
                    <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="px-4 py-2 bg-emerald-900 rounded disabled:opacity-50">←</button>
                    <span className="px-4 py-2 text-golden">Page {currentPage} of {totalPages}</span>
                    <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="px-4 py-2 bg-emerald-900 rounded disabled:opacity-50">→</button>
                </div>
                )}
            </>
        )}
      </main>

      <footer className="bg-emerald-950 border-t border-golden/20 pt-10 pb-6 mt-auto">
        <div className="text-center">
            <p className="text-golden/80 mb-4">Need help? Chat with us directly.</p>
            <a href={`https://wa.me/${waNumber}`} target="_blank" rel="noopener noreferrer" className="inline-block bg-[#25D366] text-white px-6 py-2 rounded-full font-bold shadow-lg hover:scale-105 transition-transform mb-8">Chat on WhatsApp</a>
            <div className="border-t border-golden/10 pt-6 text-sm text-golden/50">© 2024 Your Store. Powered by <a href="https://webcard.in" target="_blank" rel="noopener noreferrer" className="font-bold text-golden hover:underline">Webcard.in</a></div>
        </div>
      </footer>

      {/* CART MODAL */}
      {isCartOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex justify-end">
          <div className="bg-emerald-950 w-full max-w-md h-full border-l border-golden/30 flex flex-col animate-slide-in">
            <div className="p-4 border-b border-golden/20 flex justify-between items-center bg-emerald-900/50">
              <h2 className="text-2xl font-black text-golden">Your Cart</h2>
              <button onClick={() => setIsCartOpen(false)} className="text-golden text-2xl hover:text-white">&times;</button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4">
              {cart.length === 0 ? (
                <div className="text-center mt-20 text-golden/50">Your cart is empty.</div>
              ) : (
                cart.map((item, index) => (
                  <div key={`${item.id}-${item.selectedSize}-${index}`} className="flex gap-4 items-center bg-emerald-900/40 p-3 rounded-lg mb-3 border border-golden/10">
                    <img src={item.imageUrl} className="w-16 h-16 object-cover rounded bg-black/20" alt={item.title} />
                    <div className="flex-1">
                      <h4 className="font-bold text-sm text-white">{item.title}</h4>
                      {item.selectedSize && <div className="text-[10px] text-golden bg-golden/20 px-1.5 py-0.5 rounded inline-block mb-1">Size: {item.selectedSize}</div>}
                      <p className="text-white/80 font-bold">₹{item.price} <span className="text-xs text-golden/60">x{item.qty}</span></p>
                    </div>
                    <button onClick={() => removeFromCart(item.id, item.selectedSize)} className="text-red-400 hover:text-red-300 px-2 py-1 text-xl">×</button>
                  </div>
                ))
              )}
            </div>

            {cart.length > 0 && (
              <div className="p-6 bg-emerald-900 border-t border-golden/30">
                <div className="flex justify-between mb-4 text-lg">
                  <span className="text-golden/80">Total Amount:</span><span className="font-black text-white">₹{cartTotal.toFixed(2)}</span>
                </div>
                <button onClick={handleCheckoutClick} className="w-full bg-golden text-emerald-950 text-xl py-4 rounded-xl font-black uppercase tracking-wider hover:bg-golden-light shadow-[0_0_20px_rgba(255,215,0,0.3)] transition-all transform hover:-translate-y-1">BUY NOW VIA UPI</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* PAYMENT MODAL */}
      {showPaymentModal && (
        <div className="fixed inset-0 z-[60] bg-emerald-950/95 flex items-center justify-center p-4 backdrop-blur-md">
          <div className="bg-emerald-900/50 border border-golden/20 p-8 md:p-10 rounded-xl max-w-md w-full text-center shadow-2xl">
            <h3 className="text-xl font-medium tracking-wide text-golden mb-1 uppercase">Secure Checkout</h3>
            <div className="py-4 border-y border-golden/10 mb-6"><p className="text-white text-3xl font-light tracking-tight">₹{cartTotal.toFixed(2)}</p></div>
            <p className="text-white/80 mb-8 text-sm leading-relaxed font-light">Please complete your transaction using the UPI app. Once successful, kindly share the receipt with us on WhatsApp.</p>
            <div className="space-y-4">
              <button onClick={handleSendScreenshot} className="w-full bg-golden text-emerald-950 py-3.5 rounded font-medium hover:bg-golden-light">Share Receipt on WhatsApp</button>
              <button onClick={() => setShowPaymentModal(false)} className="w-full text-white/50 text-xs uppercase hover:text-golden">Cancel Order</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;